import { useState } from "react";
import { useNavigate } from "react-router";
import { findRouteApp } from "../constants/routes";

// Check if route is an external URL
const isExternalURL = (route: string): boolean => {
  return route.startsWith("http://") || route.startsWith("https://");
};

interface SmartNavProps {
  onAfterNav?: () => void;
  showConfirmation?: boolean;
}

// Custom navigation function
export const useSmartNavigation = ({
  onAfterNav = () => {},
  showConfirmation = false,
}: SmartNavProps = {}) => {
  const navigate = useNavigate();
  const [showDialog, setShowDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<{
    route: string;
    openInNewTab?: boolean;
  } | null>(null);

  const performNavigation = (route: string, openInNewTab: boolean = false) => {
    // Handle external URLs
    if (isExternalURL(route)) {
      if (openInNewTab) {
        window.open(route, "_blank");
      } else {
        window.location.href = route;
      }
    } else {
      const currentRouteApp = findRouteApp(window.location.pathname);
      const targetRouteApp = findRouteApp(route);

      // If navigating to a different app, do a full page load
      if (currentRouteApp && targetRouteApp && currentRouteApp !== targetRouteApp) {
        window.location.assign(route);
        return;
      }

      // Same app navigation - use router navigation
      navigate(route);
    }
    onAfterNav();
  };

  const navigateTo = (route: string, openInNewTab: boolean = false) => {
    if (showConfirmation) {
      setPendingNavigation({ route, openInNewTab });
      setShowDialog(true);
    } else {
      performNavigation(route, openInNewTab);
    }
  };

  const confirmNavigation = () => {
    if (pendingNavigation) {
      performNavigation(pendingNavigation.route, pendingNavigation.openInNewTab);
    }
  };

  return {
    navigateTo,
    confirmNavigation,
    showDialog,
    setShowDialog,
  };
};
